import { AccessManager, } from "shared";
export class HoleScoreEditor {
    constructor(deps) {
        this.deps = deps;
    }
    open(hole, holeIndex) {
        document.querySelector(".hole-score-popup-backdrop")?.remove();
        return new Promise((resolve) => {
            const roster = this.deps.getRoster();
            const currentScores = this.deps.parseGrossByRosterIndex(hole.score);
            const access = AccessManager.fromBrowserStorage(this.deps.storedScoreCard);
            const canEditScores = access.canEditScores();
            const par = this.normalizePar(hole.par);
            // Missing scores are shown as par in the working copy. Nothing is persisted
            // until Save is pressed.
            const workingScores = roster.map((player, rosterIndex) => {
                if (!this.deps.isPlayerActiveForScoring(player))
                    return null;
                return currentScores[rosterIndex] ?? par;
            });
            let selectedRosterIndex = null;
            let showAllScores = false;
            const backdrop = document.createElement("div");
            backdrop.className = "hole-score-popup-backdrop";
            const popup = document.createElement("div");
            popup.className = "hole-score-popup hole-score-editor";
            const title = document.createElement("h3");
            title.textContent = canEditScores
                ? `Hole ${hole.holeNum} Scores — Par ${par}`
                : `Hole ${hole.holeNum} Scores — Par ${par} (read only)`;
            popup.appendChild(title);
            const playersContainer = document.createElement("div");
            playersContainer.className = "hole-score-player-list";
            popup.appendChild(playersContainer);
            const selector = document.createElement("div");
            selector.className = "hole-score-selector";
            selector.hidden = true;
            popup.appendChild(selector);
            const buttonRow = document.createElement("div");
            buttonRow.className = "hole-score-popup-buttons";
            const cancelButton = document.createElement("button");
            cancelButton.type = "button";
            cancelButton.textContent = "Cancel";
            const clearButton = document.createElement("button");
            clearButton.type = "button";
            clearButton.textContent = "Clear";
            const saveButton = document.createElement("button");
            saveButton.type = "button";
            saveButton.textContent = "Save";
            cancelButton.addEventListener("click", () => {
                backdrop.remove();
                resolve({ status: "cancelled", editedHoleIndex: holeIndex });
            });
            clearButton.disabled = !canEditScores;
            saveButton.disabled = !canEditScores;
            clearButton.addEventListener("click", () => {
                if (!canEditScores)
                    return;
                roster.forEach((player, rosterIndex) => {
                    if (this.deps.isPlayerActiveForScoring(player)) {
                        workingScores[rosterIndex] = null;
                    }
                });
                selectedRosterIndex = null;
                showAllScores = false;
                render();
            });
            saveButton.addEventListener("click", async () => {
                if (!canEditScores)
                    return;
                const activeScores = roster
                    .map((player, rosterIndex) => ({
                    active: this.deps.isPlayerActiveForScoring(player),
                    score: workingScores[rosterIndex],
                }))
                    .filter((entry) => entry.active)
                    .map((entry) => entry.score);
                const hasAnyScore = activeScores.some((score) => score !== null);
                if (!hasAnyScore) {
                    await this.deps.persistRawHoleScore(hole, "", holeIndex);
                    backdrop.remove();
                    resolve({ status: "saved", editedHoleIndex: holeIndex });
                    return;
                }
                if (activeScores.some((score) => score === null)) {
                    alert("Enter a score for each player, or clear the hole.");
                    return;
                }
                if (activeScores.some((score) => score === null ||
                    !Number.isInteger(score) ||
                    score < 1 ||
                    score > 9)) {
                    alert("Scores must be whole numbers from 1 to 9.");
                    return;
                }
                const newRaw = roster
                    .map((player, rosterIndex) => this.deps.isPlayerActiveForScoring(player)
                    ? String(workingScores[rosterIndex])
                    : "0")
                    .join("");
                await this.deps.persistRawHoleScore(hole, newRaw, holeIndex);
                backdrop.remove();
                resolve({ status: "saved", editedHoleIndex: holeIndex });
            });
            buttonRow.appendChild(cancelButton);
            if (canEditScores) {
                buttonRow.appendChild(clearButton);
                buttonRow.appendChild(saveButton);
            }
            popup.appendChild(buttonRow);
            backdrop.appendChild(popup);
            document.body.appendChild(backdrop);
            const render = () => {
                playersContainer.innerHTML = "";
                roster.forEach((player, rosterIndex) => {
                    const isWithdrawn = this.deps.isPlayerWithdrawn(player.id);
                    const isCheckedIn = this.deps.isPlayerCheckedIn(player.id);
                    const isActive = this.deps.isPlayerActiveForScoring(player);
                    const row = document.createElement("div");
                    row.className = "hole-score-popup-row";
                    row.classList.toggle("inactive-player-score-row", !isActive);
                    row.classList.toggle("selected-player-score-row", selectedRosterIndex === rosterIndex);
                    const label = document.createElement("div");
                    label.className = "hole-score-player-name";
                    if (isWithdrawn) {
                        label.textContent = `${player.nickName} (withdrawn)`;
                    }
                    else if (!isCheckedIn) {
                        label.textContent = `${player.nickName} (not checked in)`;
                    }
                    else {
                        label.textContent = player.nickName;
                    }
                    const scoreButton = document.createElement("button");
                    scoreButton.type = "button";
                    scoreButton.className = "hole-score-current-value";
                    scoreButton.disabled = !isActive || !canEditScores;
                    scoreButton.textContent =
                        workingScores[rosterIndex] === null
                            ? "—"
                            : String(workingScores[rosterIndex]);
                    scoreButton.setAttribute("aria-label", `Edit ${player.nickName}'s score`);
                    scoreButton.addEventListener("click", () => {
                        if (!isActive || !canEditScores)
                            return;
                        selectedRosterIndex = rosterIndex;
                        showAllScores = false;
                        render();
                    });
                    row.appendChild(label);
                    row.appendChild(scoreButton);
                    playersContainer.appendChild(row);
                });
                renderSelector();
            };
            const renderSelector = () => {
                selector.innerHTML = "";
                if (selectedRosterIndex === null || !canEditScores) {
                    selector.hidden = true;
                    return;
                }
                const selectedPlayer = roster[selectedRosterIndex];
                if (!selectedPlayer) {
                    selector.hidden = true;
                    return;
                }
                selector.hidden = false;
                const heading = document.createElement("div");
                heading.className = "hole-score-selector-heading";
                heading.textContent = selectedPlayer.nickName;
                selector.appendChild(heading);
                const choices = document.createElement("div");
                choices.className = showAllScores
                    ? "hole-score-choice-grid hole-score-choice-grid-all"
                    : "hole-score-choice-grid";
                const scores = showAllScores
                    ? [1, 2, 3, 4, 5, 6, 7, 8, 9]
                    : this.primaryScoreChoices(par);
                scores.forEach((score) => {
                    const choice = document.createElement("button");
                    choice.type = "button";
                    choice.className = "hole-score-choice";
                    choice.textContent = String(score);
                    choice.classList.toggle("par-score-choice", score === par);
                    choice.classList.toggle("current-score-choice", workingScores[selectedRosterIndex] === score);
                    if (score === par) {
                        const parLabel = document.createElement("span");
                        parLabel.className = "hole-score-par-label";
                        parLabel.textContent = "PAR";
                        choice.appendChild(parLabel);
                    }
                    choice.addEventListener("click", () => {
                        workingScores[selectedRosterIndex] = score;
                        selectedRosterIndex = null;
                        showAllScores = false;
                        render();
                    });
                    choices.appendChild(choice);
                });
                selector.appendChild(choices);
                if (!showAllScores) {
                    const otherButton = document.createElement("button");
                    otherButton.type = "button";
                    otherButton.className = "hole-score-other-button";
                    otherButton.textContent = "Other score (1–9)";
                    otherButton.addEventListener("click", () => {
                        showAllScores = true;
                        renderSelector();
                    });
                    selector.appendChild(otherButton);
                }
            };
            render();
            if (canEditScores) {
                saveButton.focus();
            }
            else {
                cancelButton.focus();
            }
        });
    }
    normalizePar(value) {
        const par = Number(value);
        return Number.isInteger(par) && par >= 1 && par <= 9 ? par : 4;
    }
    primaryScoreChoices(par) {
        const start = Math.max(1, Math.min(par - 2, 5));
        return [start, start + 1, start + 2, start + 3, start + 4];
    }
}
//# sourceMappingURL=HoleScoreEditor.js.map